import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, formatEUR } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { Reveal } from "../components/Reveal";

const OrderSuccess = () => {
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get("session_id");
  const { clearCart } = useStore();
  const [state, setState] = useState("pending");
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    let timer;
    const tick = async () => {
      try {
        const st = await api.get(`/payments/status/${sessionId}`);
        if (st.payment_status === "paid") {
          setState("paid");
          const o = await api.get(`/orders/by-session/${sessionId}`).catch(() => null);
          if (o) setOrder(o);
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        if (st.status === "expired" || st.status === "failed") { setState(st.status); return; }
      } catch (e) { /* retry */ }
      tries += 1;
      if (tries < 10) timer = setTimeout(tick, 3000);
      else setState("timeout");
    };
    tick();
    return () => clearTimeout(timer);
  }, [sessionId, clearCart]);

  return (
    <main className="pt-32 sm:pt-40 pb-24 px-4 min-h-screen" data-testid="order-success-page">
      <div className="max-w-2xl mx-auto text-center">
        {state === "pending" && (
          <>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500" data-testid="order-pending-label">Vérification du paiement…</p>
            <p className="mt-4 text-sm text-neutral-400">La confirmation peut prendre quelques secondes. Ne fermez pas cette page.</p>
          </>
        )}

        {state === "paid" && (
          <Reveal>
            <div className="inline-flex items-center justify-center w-20 h-20 border border-gold/40 rounded-full mb-8" data-testid="order-success-badge">
              <svg viewBox="0 0 24 24" className="w-9 h-9 text-gold" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-6xl text-white">
              Commande <span className="text-gold">confirmée</span>
            </h1>
            {order && (
              <>
                <p className="mt-4 font-mono text-sm text-neutral-400" data-testid="order-success-number">
                  N° <span className="text-white">{order.order_number}</span> — un email de confirmation a été envoyé à <span className="text-white">{order.customer.email}</span>
                </p>
                <div className="mt-10 border border-line bg-coal p-6 sm:p-8 text-left" data-testid="order-success-summary">
                  {order.items.map((it, i) => (
                    <div key={i} className="flex justify-between py-2 text-sm border-b border-line last:border-0">
                      <span className="text-neutral-300">{it.name} — {it.size}{it.color ? ` · ${it.color}` : ""}{it.custom_text ? ` · N° ${it.custom_text}` : ""} × {it.quantity}</span>
                      <span className="font-mono text-silver">{formatEUR(it.unit_price * it.quantity)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between py-2 text-sm">
                    <span className="text-neutral-400">Livraison</span>
                    <span className="font-mono text-silver">{order.shipping_fee === 0 ? "Offerte" : formatEUR(order.shipping_fee)}</span>
                  </div>
                  <div className="flex justify-between pt-3 text-base font-bold">
                    <span className="text-white uppercase font-display">Total payé</span>
                    <span className="font-mono text-white" data-testid="order-success-total">{formatEUR(order.total)}</span>
                  </div>
                  <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-500">
                    Livraison à : {order.customer.address}, {order.customer.postal_code} {order.customer.city}
                  </p>
                </div>
              </>
            )}
            <Link to="/boutique" data-testid="order-success-continue" className="mt-10 inline-block bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] px-10 py-4 hover:bg-gold transition-colors">
              Continuer mes achats
            </Link>
          </Reveal>
        )}

        {(state === "timeout" || state === "expired" || state === "failed") && (
          <>
            <p className="font-display font-extrabold uppercase text-outline text-5xl">Paiement non confirmé</p>
            <p className="mt-4 text-sm text-neutral-400">
              Si vous avez été débité, votre commande est enregistrée et vous recevrez l'email de confirmation.
              En cas de doute, contactez-nous.
            </p>
            <Link to="/contact" data-testid="order-issue-contact" className="mt-8 inline-block border border-lineActive text-white font-mono text-xs uppercase tracking-[0.25em] px-10 py-4 hover:border-white transition-colors">
              Nous contacter
            </Link>
          </>
        )}
      </div>
    </main>
  );
};

export default OrderSuccess;
